import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { withRouter, Prompt } from 'react-router-dom'
import { FormattedMessage, FormattedHTMLMessage, injectIntl, intlShape, FormattedNumber } from 'react-intl'
import { toInteger, isEmpty, get, keyBy, set, debounce } from 'lodash'

import { callApi } from '../services/fetchService'
import { decodePatient } from '../services/encodingService'
import { openPatientRecord } from '../actions'

import FaIcon from '../components/FaIcon'
import AutoSaveButton from '../components/AutoSaveButton'
import { Spin, Tabs, Table, Tag, Row, Col, List, InputNumber, Form, message } from 'antd'
const { TabPane } = Tabs
const { Column } = Table



/**
 * Semiology form definition
 */
const semiologyForm = [
    {
        name: "parasympathetic",
        strong: ["shyness", "anxiety", "hypersalivation", "pectusExcavatum", "mucusSecretions", "bloating", "sweats", "aerophagia", "nausea", "diarrhea"],
        weak: ["nightmares", "softCharacter", "mydriasis", "weakPulse", "lowDiastolicBP", "dry", "poorMuscles", "constipation"]
    },
    {
        name: "alphasympathetic",
        strong: ["aggressiveness", "insomnia", "redFace", "dryMouth", "palatePaleness", "tonguePalate", "quickAyelids", "coldExtremities", "dermographism", "spasmodicConstipation"],
        weak: ["adaptationProblems", "weakVoice", "myosis", "dilatedVeins", "dryHands"]
    },
    {
        name: "histamine",
        strong: ["itchings", "dermographism", "sneezing", "nasalDischarge", "allergicReactions"]
    },
    {
        name: "betasympathetic",
        strong: ["impulsiveness", "agressiveness", "hypertension", "boundingPulse", "palpitations", "dryExtremities", "fingersTremor"],
        weak: ["daydreams", "migraines", "unableToAct", "lackWishes", "lowBP", "fatigability", "lackSweating", "lackLibido"]
    },
    {
        name: "acth",
        strong: ["extroversion", "poorMemory", "earlyWakeUp", "buccalMucosa", "earsHair", "adiposity", "backAcne", "stretchMarks", "highBP"],
        weak: ["adaptationProblems", "depresiveMind", "asthenia", "lowBP", "anorexia", "lackPigmentation"]
    },
    {
        name: "cortisol",
        strong: ["chronicStress", "strongReactivity", "depressive", "stoutness", "cellulitis", "muscleWasting", "breastStriae", "stretchMarks", "ecchymosis", "skinThinness"],
        weak: ["lackReactivity", "fatigability", "asthenia", "thinness", "hoarseVoice", "ent"]
    },
    {
        name: "dopamine",
        strong: ["anxiety", "excitability", "emotivity", "paranoia", "crazyIdeas"],
        weak: ["depressive", "lackEnergy", "affective", "poorCoordination", "autism", "parkinson"]
    },
    {
        name: "aldosterone",
        strong: ["anklesSwelling", "thirst", "highBP"],
    },
    {
        name: "fsh",
        strong: ["highFeminity", "strongSensitivity", "nasolabialRedness", "violaciousPillars", "longEyeslashes", "largeAreolas", "mastosisRight", "cellulitis", "spotsElbows", "thinPilosity", "colonTenderness"],
        weak: ["smallBreast", "lackMenstruals", "smallPenis", "weakLibido", "erectyleDysfunction", "infertility"]
    },
    {
        name: "estrogens",
        strong: ["trapezoid", "waterRetention", "diffuseAdiposity", "voluminousHair", "fatScalp", "eyeslashes", "voluminousBreast", "largeNipples", "hyperlax", "softSkin", "sweatOdor", "stretchMarks", "heavyPeriods", "strongLibido"],
        weak: ["dryHair", "drySkin", "wrinkledSkin", "decreasingLibido", "clotsMenstruals"]
    },
    {
        name: "androgens",
        strong: ["fightingSpirit", "strongExercize", "inverseBody", "lowVoice", "recedingHair", "hairyChest", "hairAreolas", "hypertrohpicScars", "denseHair", "menstrualsDisorders", "growthInterruption", "peoeminentPubis"],
        weak: ["passivity", "lackDepreciation", "reducedHairness", "abdominalFat", "hyperlaxLigaments", "lossPilosity", "genitalInsufficiency"]
    },
    {
        name: "progesterone",
        strong: ["breastTension", "strongSexualDesire", "clotsEndMenstrual", "premenstrualSyndrom"],
        weak: ["faceAcne", "mastosis", "venousDisorders", "hairFace", "lackSexualDesire", "clotsStartMenstrual", "herpesOvulation"]
    },
    {
        name: "trh",
        strong: ["nervousness", "strongImagination", "colorDreams", "energeticDischarges", "blinkingEyelids", "pupilsContraction", "handsTremor", "fatFilling"],
        weak: ["selfDepreciation", "realityLoss", "relationship", "lossWeight", "muscleWasting", "sexualDisorders", "boulimia"]
    },
    {
        name: "thyroid",
        strong: ["livelyCharacter", "loosingWeight", "standingHeat", "thickHair", "erithema", "thinEyebrows", "thinTremor", "itchings", "sweats", "thyroidVolume", "thyroidNodules", "premenstrualSyndrom", "lowMenstruals"],
        weak: ["lackEnergy", "depression", "lymphatism", "coldTolerance", "thinHair", "sparseEyebrows", "thinNails", "lymhaticLegs", "drySkin", "constipation", "goiter", "heavyMenstrual"]
    },
    {
        name: "insulin",
        strong: ["bmi", "overweight", "hunger", "sweetFood", "increasedAppetite", "cellulite", "softAbdomen", "slowlyHealing"],
        weak: ["loosingAppetite"]
    },
    {
        name: "prolactin",
        strong: ["fearful", "increaseMotheringInstinct", "bulkyBreast", "largeAreolas", "wideNipples", "prolongedLactation", "flattenedFoot", "dorsalEdema", "paleSkin", "tidyUpDesire", "amenorrhea", "decreaseLibido", "sexualBehavior", "pseudoPregnancy"],
        weak: ["decreaseMotheringInstinct", "smallBreast", "irregularCycles", "stoppedLactation"]
    },
    {
        name: "gh",
        strong: ["thickTongue", "thickEyeslashes", "longNose", "thickNeck", "nailPitting"],
        weak: ["dwarfnessTisk"]
    },
    {
        name: "liver",
        weak: ["hypersalivation", "jaundice", "ammoniaBreath", "darkCircles", "cholestasis", "slowlyHealing", "drowsiness", "fatsDigesting", "darkUrines"]
    },
    {
        name: "pancreas",
        weak: ["hypersalivation", "tonsils", "parotidGlands", "thickTongue", "ent", "bloating", "mealsPains", "sweating", "mycosis", "skinRashes", "fatStools"]
    }
]

const computeSemiology = (form, data) => {
    return form.map((value) => {
        value = Object.assign({}, value)
        if (value.strong) {
            value.strongCount = value.strong.length
            if (data[value.name]) {
                value.strongSum = value.strong.reduce((result, name) => result + (data[value.name][name] || 0), 0)
                value.strongEncoded = value.strong.reduce((result, name) => result || !!data[value.name][name], false)
                value.strong = value.strongSum / (value.strongCount * 3) * 10
            } else {
                value.strongSum = 0
                value.strongEncoded = 0
                value.strong = 0
            }
        } else {
            value.strong = null
            value.strongEncoded = false
        }
        if (value.weak) {
            value.weakCount = value.weak.length
            if (data[value.name]) {
                value.weakSum = value.weak.reduce((result, name) => result + (data[value.name][name] || 0), 0)
                value.weakEncoded = value.weakCount === 0 || value.weak.reduce((result, name) => result || !!data[value.name][name], false)
                value.weak = value.weakSum / (value.weakCount * 3) * 10
            } else {
                value.weakSum = 0
                value.weakEncoded = 0
                value.weak = 0
            }
        } else {
            value.weak = null
            value.weakEncoded = false
        }
        return value
    })
}



/**
 * Encoding form inside the view
 */

class _EncodeSemiology extends Component {

    static propTypes = {
        semiology: PropTypes.object.isRequired,
        activeCategory: PropTypes.string.isRequired,
        onCategoryRedirect: PropTypes.func.isRequired,
        onFieldChange: PropTypes.func.isRequired,
        intl: intlShape.isRequired
    }

    onTabsChange = (activeTab) => {
        this.props.onCategoryRedirect(activeTab)
    }

    render() {
        const { getFieldDecorator, getFieldsValue } = this.props.form
        const { semiology, activeCategory } = this.props

        let values = getFieldsValue()
        values = isEmpty(values) ? semiology : values
        const data = keyBy(computeSemiology(semiologyForm, values), 'name')

        return (
            <Form>
                <Tabs tabPosition="left" className="semiology-tabs" onChange={this.onTabsChange} activeKey={activeCategory}>
                    {
                        semiologyForm.map((category) =>
                            <TabPane tab={<FormattedMessage id={"patient.semiology." + category.name} />} key={category.name} forceRender={true}>
                                <Row gutter={32}>
                                    {
                                        ['strong', 'weak'].map((type) =>
                                            <Col span={12} key={type}>
                                                <List size="small" className="semiology-list" dataSource={category[type]} renderItem={item => (
                                                    <List.Item>
                                                        <FormattedMessage id={"patient.semiology." + category.name + "." + item} />
                                                        {getFieldDecorator(category.name + '.' + item, {
                                                            initialValue: get(semiology, category.name + '.' + item)
                                                        })(<InputNumber min={0} max={3} size="small" style={{ width: '60px' }} />)}
                                                    </List.Item>
                                                )} header={
                                                    <div>
                                                        <strong><FormattedMessage id={"patient.semiology." + type} /></strong>
                                                        {
                                                            category[type] ? <span><strong>{data[category.name] ? data[category.name][type + "Sum"] || 0 : 0}</strong> <small>/{category[type].length * 3}</small></span> : null
                                                        }
                                                    </div>
                                                } />
                                            </Col>
                                        )
                                    }
                                </Row>
                            </TabPane>
                        )
                    }
                </Tabs>
            </Form>
        )
    }

}

const EncodeSemiology = injectIntl(Form.create({
    onFieldsChange(props, changedFields) {
        props.onFieldChange(Object.values(Object.values(changedFields)[0])[0])
    }
})(_EncodeSemiology))




/**
 * Summary inside the view
 */

class _SemiologySummary extends Component {

    static propTypes = {
        semiology: PropTypes.object,
        onCategoryRedirect: PropTypes.func.isRequired,
        intl: intlShape.isRequired
    }

    rowsConfig = (record) => ({
        style: { cursor: 'pointer' },
        onClick: (e) => this.props.onCategoryRedirect(record.name)
    })

    render() {
        const semiology = this.props.semiology || {}
        
        const data = computeSemiology(semiologyForm, semiology)

        return (
            <Table dataSource={data} rowKey="name" size="middle" pagination={false} onRow={this.rowsConfig}>
                <Column title={<FormattedMessage id="patient.semiology.neuroColumn"/>} key="name" render={(record) => (<FormattedMessage id={"patient.semiology." + record.name} />)} />
                <Column title={<FormattedMessage id="patient.semiology.typeColumn"/>} render={
                    (record) => {
                        if ((record.strongEncoded || record.strong !== null) && (record.weakEncoded || record.weak !== null) && record.strong !== record.weak) {
                            return record.strong >= record.weak
                                ? <Tag color="volcano" style={{ cursor: "unset" }}><FormattedMessage id="patient.semiology.strong" /></Tag>
                                : <Tag color="blue" style={{ cursor: "unset" }}><FormattedMessage id="patient.semiology.weak" /></Tag>
                        }
                        return ''
                    }
                } />
                <Column title={<FormattedHTMLMessage id="patient.semiology.strongColumn"/>} render={
                    (record) => record.strongEncoded
                        ? record.strong > record.weak
                            ? <strong style={{ color: "#fa541c" }}><FormattedNumber value={record.strong} maximumFractionDigits={1} /></strong>
                            : <FormattedNumber value={record.strong} maximumFractionDigits={1} />
                        : record.strong !== null ? '' : '/'
                } />
                <Column title={<FormattedHTMLMessage id="patient.semiology.weakColumn"/>} render={
                    (record) => record.weakEncoded
                    ? record.strong < record.weak
                        ? <strong style={{ color: "#1890ff" }}><FormattedNumber value={record.weak} maximumFractionDigits={1} /></strong>
                        : <FormattedNumber value={record.weak} maximumFractionDigits={1} />
                    : record.weak !== null ? '' : '/'
                } />
            </Table>
        )
    }

}

const SemiologySummary = injectIntl(_SemiologySummary)



/**
 * Semiology view
 */

class PatientSemiology extends Component {

    static propTypes = {
        openPatientRecord: PropTypes.func.isRequired,
        history: PropTypes.object.isRequired,
        match: PropTypes.object.isRequired,
        intl: intlShape.isRequired
    }
    
    state = {
        patientId: null,
        patient: {},
        loading: false,
        activeCategory: semiologyForm[0].name,
        activeTab: 'summary',
        dirty: false
    }

    fetch = () => {
        const { intl, openPatientRecord } = this.props
        this.setState({ loading: true })
        callApi({
            endpoint: 'patients/' + this.state.patientId
        }).then((data) => {
            decodePatient(data).then((decoded) => {
                if (!decoded.semiology) {
                    decoded.semiology = {}
                }
                this.setState({
                    patient: decoded,
                    loading: false
                })
                openPatientRecord(decoded)
            })
        }).catch((data) => {
            message.error(intl.formatMessage({id: 'misc.unknownError'}))
            this.setState({ loading: false })
        })
    }

    componentWillReceiveProps(nextProps) {
        let id = toInteger(nextProps.match.params.id)
        if (this.state.patientId !== id) {
            this.setState({ patientId: id }, () => {
                this.fetch()
            })
        }
    }

    componentWillMount() {
        let id = toInteger(this.props.match.params.id)
        this.setState({ patientId: id })
    }

    componentDidMount() {
        this.fetch()
    }

    saveSemiology = () => {
        const { intl } = this.props
        callApi({
            endpoint: 'patients/' + this.state.patientId,
            body: { semiology: this.state.patient.semiology },
            method: 'POST'
        }).catch((err) => {
            message.error(intl.formatMessage({id: 'misc.unknownError'}))
        })
        this.setState({ dirty: false })
    }

    debouncedFunction = debounce(this.saveSemiology, 3 * 1000, { maxWait: 30 * 1000 })
    onFieldChange = (changedField) => {
        const { patient } = this.state
        set(patient.semiology, changedField.name, changedField.value)
        this.setState({
            patient: patient,
            dirty: true
        })
        this.debouncedFunction()
    }

    forceSave = () => {
        const { dirty } = this.state
        if (dirty === true) {
            this.saveSemiology()
        }
    }

    onCategoryRedirect = (category) => {
        this.setState({
            activeCategory: category,
            activeTab: 'encode'
        })
    }

    onTabsChange = (activeTab) => {
        this.setState({ activeTab: activeTab });
    }

    render() {
        const { patient, loading, activeCategory, activeTab, dirty } = this.state
        const { intl } = this.props

		if (isEmpty(patient) || loading) {
			return (
                <div style={{ textAlign: 'center', marginTop: '50px' }}>
                    <Spin size="large" />
                </div>
            )
        }
        
        return (
            <div>
               <h1><FaIcon icon="user" fixedWidth /><FormattedHTMLMessage id="patient.semiology.title" values={patient} /></h1>
               <Tabs defaultActiveKey="summary" activeKey={activeTab} onChange={this.onTabsChange}>
                    <TabPane tab={<span><FaIcon icon="pencil" fixedWidth /> <FormattedMessage id="patient.semiology.encode" /></span>} key="encode">
                        <EncodeSemiology semiology={patient.semiology} onCategoryRedirect={this.onCategoryRedirect} activeCategory={activeCategory} onFieldChange={this.onFieldChange} />
                    </TabPane>
                    <TabPane tab={<span><FaIcon icon="clipboard-list" fixedWidth /> <FormattedMessage id="patient.semiology.summary" /></span>} key="summary">
                        <SemiologySummary semiology={patient.semiology} onCategoryRedirect={this.onCategoryRedirect} />
                    </TabPane>
                </Tabs>
                <AutoSaveButton isDirty={dirty} onClick={this.forceSave} />
                <Prompt when={dirty} message={intl.formatMessage({id: 'misc.unsavedData'})} />
            </div>
        )
    }

}

export default injectIntl(withRouter(connect(null, {
    openPatientRecord
})(PatientSemiology)))


// WEBPACK FOOTER //
// ./src/views/PatientSemiology.js